<template>
  <div class="LdFaceDetection">
    <div
      v-show="!devicesError"
      class="text-center"
    >
      <h3 class="font18">
        {{ tips }}
      </h3>

      <p>请不要配戴眼镜</p>

      <div class="fd-container">
        <div class="fill">
          <video
            :id="`${NAME_SPACE}-video`"
            class="video"
            autoplay
            muted
            loop
            webkit-playsinline
            playsinline
          />
          <canvas
            :id="`${NAME_SPACE}-overlay`"
            class="overlay"
          />
        </div>
      </div>

      <p>我们正在收集您的面部特征信息</p>

      <div v-if="test">
        <van-cell-group title="统计信息">
          <van-cell
            title="Time"
            :value="`${time} ms`"
          />
          <van-cell
            title="Estimated Fps"
            :value="fps"
          />

          <van-cell
            title="人脸识别分数"
            :value="`${score}%`"
            :label="`分数大于 ${scoreThreshold}，进行活体检测`"
          />

          <van-cell
            title="识别成功回调的图片（150 * 200）"
            label="目前策略：取分数最高的图片"
          >
            <img
              v-if="detectionsSerial.length > 0"
              :src="getBestScoreImageByDetections() && getBestScoreImageByDetections().src"
              alt=""
              srcset=""
            >
          </van-cell>

          <van-cell title="是否开启活体检测">
            <van-switch
              v-model="openedLivenessDetection"
              size="18"
            />
          </van-cell>

          <van-cell
            title="活体检测策略"
            is-link
            @click="showLivenessDetectionStrategyPicker = true"
          >
            {{ LivenessDetectionEvent[currentLivenessDetectionStrategyIndex] }}
          </van-cell>

          <van-cell title="目前是否张嘴">
            <span
              v-if="openedMouse"
              class="green"
            >正在张嘴</span>
            <span
              v-else
              class="red"
            >没有张嘴</span>
          </van-cell>
        </van-cell-group>

        <van-popup
          v-model="showLivenessDetectionStrategyPicker"
          round
          position="bottom"
        >
          <van-picker
            show-toolbar
            :columns="LivenessDetectionEvent"
            @cancel="showLivenessDetectionStrategyPicker = false"
            @confirm="onLivenessDetectionStrategyConfirm"
          />
        </van-popup>
      </div>
    </div>

    <van-empty
      v-if="devicesError"
      image="error"
    >
      <div class="text-center">
        <h3>请求网络摄像头访问时出错</h3>
        <p class="gray">
          {{ devicesError }}
        </p>
      </div>
    </van-empty>
  </div>
</template>

<script>
/**
 * 人脸识别组件
 * 本地调试必须 在https地址下运行
 * 示例参考: https://github.com/justadudewhohacks/face-api.js
 * Model模型放在：public/tfjs-model 下，目前只放了人脸和地标检测的模型
 *
 * 人脸地标，有68个点，每个点都固定代表一个位置
 * 可把数据在 https://echarts.apache.org/examples/zh/editor.html?c=scatter-simple
 * 一个个展示出来，用于做活体检测的对比
 *
 * 例如： landmarks.getMouth() 返回嘴巴的地标点
 * 其中第15个点 和 第19个点分别是 上嘴唇的下边中点 和 下嘴唇的上边中点
 * 张张嘴：判断该两个点的距离，大于25像素即活体测试通过
 */
import Vue from 'vue'
import * as faceapi from 'face-api.js'
import { Empty, Cell, CellGroup, Switch, Picker, Popup, ImagePreview } from 'vant'
import { API_DEFAULT_CONFIG } from 'Config'
import { localget } from 'Utils/storage.js'
import MedioPollyfill from './media'
import axios from 'axios'
import VConsole from 'vconsole'
import { isH5 } from '@/common/utils/plus'

Vue.use(ImagePreview)

const NAME_SPACE = 'LdFaceDetection'
const defaultTips = '请将正脸置于取景框内'
const LivenessDetectionEvent = ['张张嘴', '眨眨眼']

/**
 * @returns {File}
 * 将base64转换为文件对象
 * 直接存到 new FormData() 里
 * file.append("file", blob)
 **/
function dataURLtoFile(dataurl, filename) {
  var arr = dataurl.split(',')
  var mime = arr[0].match(/:(.*?);/)[1]
  var bstr = atob(arr[1])
  var n = bstr.length
  var u8arr = new Uint8Array(n)
  while(n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  //转换成file对象
  return new File([u8arr], filename, { type: mime })
  //转换成成blob对象
  //return new Blob([u8arr],{type:mime});
}

/**
 * @returns {String} base64URL
 * canvas获取摄像头图片
 **/
function getImage (video) {
  let canvas = document.createElement('canvas')
  canvas.width = 150
  canvas.height = 200
  let context = canvas.getContext('2d')
  context.drawImage(video, 0, 0, 150, 200)
  let src = canvas.toDataURL('image/png')

  return src
}

export default {
  name: NAME_SPACE,
  components: {
    [Empty.name]: Empty,
    [CellGroup.name]: CellGroup,
    [Cell.name]: Cell,
    [Switch.name]: Switch,
    [Picker.name]: Picker,
    [Popup.name]: Popup,
  },
  props: {
    test: Boolean, // 开启调试信息
    isTrInvoce: Boolean, // 开启调试信息
    base64: Boolean, // 是否返回base64
  },
  data() {
    return {
      NAME_SPACE,
      modelLoading: true,
      LivenessDetectionEvent,
      showLivenessDetectionStrategyPicker: false,
      currentLivenessDetectionStrategyIndex: 0,
      videoEl: null,
      canvasEl: null,
      updateResultsTimer: null,
      devicesError: false,
      score: 0,
      scoreThreshold: 95,
      openedLivenessDetection: true,
      openedMouse: false,
      tips: defaultTips,
      forwardTimes: [],
      time: 0,
      fps: 0,
      detectionsSerial: [],
    }
  },
  async created () {
    // new VConsole()
    MedioPollyfill()

    this.$toast.loading({ message: '正在初始化...', duration: 0 })

    let baseURL = isH5() ? 'tfjs-model/' : `${process.env.VUE_APP_addressIp}/tfjs-model/`

    //人脸识别初始化配置
    await this.getCurrentFaceDetectionNet().load(baseURL)        //神经网络加载
    await faceapi.loadFaceLandmarkModel(baseURL)            //人脸识别必须
    // await faceapi.loadFaceRecognitionModel('tfjs-model/');        //人脸提取必须

    this.$toast.clear()
  },
  async mounted () {
    this.videoEl = document.getElementById(`${NAME_SPACE}-video`)
    this.canvasEl = document.getElementById(`${NAME_SPACE}-overlay`)

    console.log('mounted')

    //监听摄像头播放事件
    this.videoEl.addEventListener('play', this.updateResults)
    try {
      let stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      })

      // 旧的浏览器可能没有srcObject
      if ('srcObject' in this.videoEl) {
        this.videoEl.srcObject = stream
      } else {
        // 防止再新的浏览器里使用它，应为它已经不再支持了
        this.videoEl.src = window.URL.createObjectURL(stream)
      }
      //如果数据已经加载了，就播放
      this.videoEl.onloadedmetadata = (e) => {
        this.videoEl.play()
      }

    } catch (error) {
      console.error(error.name + ': ' + error.message)
      this.devicesError = error.name + ': ' + error.message
    }


  },
  methods: {
    onLivenessDetectionStrategyConfirm (value, index) {
      this.showLivenessDetectionStrategyPicker = false

      this.currentLivenessDetectionStrategyIndex = index
    },
    updateTimeStats (timeInMs) {
      this.forwardTimes = [timeInMs].concat(this.forwardTimes).slice(0, 30)
      const avgTimeInMs = this.forwardTimes.reduce((total, t) => total + t) / this.forwardTimes.length

      this.time = Math.round(avgTimeInMs)
      this.fps = faceapi.utils.round(1000 / avgTimeInMs)
    },
    updateResults () {
      console.log('updateResults')
      this.displayingDetectionResult(this.videoEl, this.canvasEl)

      this.updateResultsTimer = setInterval(() => {
        this.displayingDetectionResult(this.videoEl, this.canvasEl)
      }, 800)   // 第秒种输出一次结果
    },
    //识别的结果处理
    async displayingDetectionResult(video, canvas) {
      console.log('displayingDetectionResult')
      console.log(faceapi.nets.ssdMobilenetv1)
      if (!this.faceapiIsReady()) {
        return
      }

      console.log('faceapiIsReady: true')

      const ts = Date.now()

      const options = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 })
      // detectAllFaces detectSingleFace
      const detections = await faceapi.detectSingleFace(video, options).withFaceLandmarks()
      // const detections = await faceapi.detectAllFaces(video, options).withFaceLandmarks().withFaceDescriptors();

      if (!detections || detections.length <= 0) {
        this.score = 0
        this.tips = defaultTips
        this.openedMouse = false
        return
      }

      if (this.test) {
        this.updateTimeStats(Date.now() - ts)

        const dims = faceapi.matchDimensions(canvas, video, true)
        const resizedDetections = faceapi.resizeResults(detections, dims)
        faceapi.draw.drawDetections(canvas, resizedDetections)             //用方框标出人脸的位置
        faceapi.draw.drawFaceLandmarks(canvas, resizedDetections)       //用点位标出人脸的位置

        console.log(detections.detection)
      }

      this.score = detections.detection.score * 100

      if (this.score > this.scoreThreshold) {
        this.tips = `请保持面部正面状态同时${LivenessDetectionEvent[this.currentLivenessDetectionStrategyIndex]}`

        this.handlerLivenessDetection(detections.landmarks, detections)
      } else {
        this.tips = defaultTips
        this.openedMouse = false
      }

    },
    /**
     * 活体检测
     *  1.张张嘴
     */
    handlerLivenessDetection (landmarks, detections) {
      let mouthPosts = landmarks.getMouth()

      let topMouthCenterPoint = mouthPosts[14],
        bottomMouthCenterPoint = mouthPosts[18]

      let diffX = Math.abs(topMouthCenterPoint.x - bottomMouthCenterPoint.x),
        diffY = Math.abs(topMouthCenterPoint.y - bottomMouthCenterPoint.y)

      // 上嘴唇的中间点 到 下嘴唇的中间点的距离 单位：像素px
      let distanceBetweenPoint = Math.pow(Math.pow(diffX, 2) + Math.pow(diffY, 2) , 0.5)

      this.detectionsSerial.push({
        detections,
        distanceBetweenPoint,
        src: getImage(this.videoEl),
      })

      if (distanceBetweenPoint > 25) {
        this.openedMouse = true

        this.success()
      } else {
        this.openedMouse = false
      }
    },
    /**
     * 获取最高分数 最合适的图片
     * @returns {String} 图片的 base64 字符串
     */
    getBestScoreImageByDetections () {
      return this.detectionsSerial.sort((a_d, b_d) => {
        return b_d.detections.detection.score - a_d.detections.detection.score
      })[0]
    },
    getCurrentFaceDetectionNet() {
      return faceapi.nets.ssdMobilenetv1
    },
    // 人脸api是否准备好
    faceapiIsReady() {
      return !!this.getCurrentFaceDetectionNet().params
    },
    async uploaderFun (file, timeout) {
      try {
        let formData = new FormData()
        let token = localget('access_token')
        formData.append('file', file)
        formData.append('token', token)
        formData.append('sysCode', 7000)
        formData.append('timeout', timeout)

        let { data = {} } = await axios({
          url: API_DEFAULT_CONFIG.URL.uploadFileUrl,
          method: 'post',
          data: formData,
          contentType: false,
          processData: false,
        })

        if (data.errCode == '1') {
          return data.data
        } else {
          throw data
        }

      } catch (error) {
        throw error
      }
    },
    async success () {
      clearInterval(this.updateResultsTimer)

      let imgFile = dataURLtoFile(this.getBestScoreImageByDetections().src, `train-face-detection-${new Date().getTime()}.png`)

      if (this.isTrInvoce){
        //火车票发票人脸识别获取base64 字符串,不要图片url,不保存用户人脸信息，获取后直接发送给12306进行人脸识别
        let imageHttpSrc = await this.uploaderFun(imgFile, -1)
        this.$emit('invoiceSuccess', this.getBestScoreImageByDetections())
      } else if (this.base64) {
        this.$emit('success', (this.getBestScoreImageByDetections().src || '').split(',')[1] || '')
      } else {
        let imageHttpSrc = await this.uploaderFun(imgFile, -1)
        this.$emit('success', imageHttpSrc)
      }

    },
    // 当前video标签截图
    downloadImage () {
      var url = window.URL.createObjectURL(
        dataURLtoFile(getImage(this.videoEl))
      )
      var a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = new Date() * 1 + '.png'
      document.body.appendChild(a)
      a.click()
      setTimeout(function () {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }, 1000)
    },
  },
  // 销毁 video canvas
  destroyed () {
    this.videoEl.pause()
    this.videoEl.removeEventListener('play', this.updateResults)
    this.videoEl.parentElement.removeChild(this.videoEl)
    this.canvasEl.parentElement.removeChild(this.canvasEl)
    clearInterval(this.updateResultsTimer)
  },
}
</script>

<style lang="less">
.LdFaceDetection {

  .fd-container {
    position: relative;
    width: 100%;
    height: 265px;
    transform: rotateY(180deg);
    overflow: auto;

    &:after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      border: 80px solid #fff;
      border-radius: 50%;
      width: 200px;
      height: 200px;
      background: transparent;
    }

    .fill {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 200px;
      height: 100%;

      .video {
        width: 100%;
        height: 100%;
        // border: 1px solid red; // test
      }

      .overlay {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        // border: 1px solid blue; // test
      }
    }


  }

}
</style>
